import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Timer, AlertTriangle, AlertCircle, Info, Minus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AvgResolutionTimeProps {
  incidents: any[];
  catalogTasks: any[];
}

interface PriorityTicketSummary {
  priority: string;
  label: string;
  icon: any;
  totalOpened: number;
  totalResolved: number;
  percentage: number;
}

const priorityConfig = {
  '1 - Critical': { 
    label: 'Critical', 
    icon: AlertTriangle, 
    color: 'text-red-600',
    dotColor: 'bg-red-500'
  },
  '2 - High': { 
    label: 'High', 
    icon: AlertCircle, 
    color: 'text-orange-600',
    dotColor: 'bg-orange-500'
  },
  '3 - Moderate': { 
    label: 'Moderate', 
    icon: Info, 
    color: 'text-yellow-600',
    dotColor: 'bg-yellow-500'
  },
  '4 – Normal': { 
    label: 'Normal', 
    icon: Minus, 
    color: 'text-gray-600',
    dotColor: 'bg-gray-500'
  }
};

export function AvgResolutionTime({ incidents, catalogTasks }: AvgResolutionTimeProps) {
  const calculatePriorityBreakdown = (tickets: any[]): PriorityTicketSummary[] => {
    const priorities = ['1 - Critical', '2 - High', '3 - Moderate', '4 – Normal'];
    
    return priorities.map(priority => {
      const priorityTickets = tickets.filter(ticket => ticket.Priority === priority);
      const totalOpened = priorityTickets.length;
      const totalResolved = priorityTickets.filter(ticket => {
        const opened = ticket.Opened;
        const closed = ticket.Closed;
        return opened && closed;
      }).length;
      
      const percentage = totalOpened > 0 ? Math.round((totalResolved / totalOpened) * 100) : 0;
      
      return {
        priority,
        label: priorityConfig[priority as keyof typeof priorityConfig].label,
        icon: priorityConfig[priority as keyof typeof priorityConfig].icon,
        totalOpened,
        totalResolved,
        percentage
      };
    });
  };

  const incidentBreakdown = useMemo(() => {
    return calculatePriorityBreakdown(incidents);
  }, [incidents]);

  const taskBreakdown = useMemo(() => {
    return calculatePriorityBreakdown(catalogTasks);
  }, [catalogTasks]);

  const getTotalSummary = (breakdown: PriorityTicketSummary[]) => {
    const totalOpened = breakdown.reduce((sum, item) => sum + item.totalOpened, 0);
    const totalResolved = breakdown.reduce((sum, item) => sum + item.totalResolved, 0);
    const overallPercentage = totalOpened > 0 ? Math.round((totalResolved / totalOpened) * 100) : 0;
    
    return { totalOpened, totalResolved, overallPercentage };
  };

  const incidentSummary = getTotalSummary(incidentBreakdown);
  const taskSummary = getTotalSummary(taskBreakdown);

  const TableRow = ({ item, isTotal = false }: { item: PriorityTicketSummary | any; isTotal?: boolean }) => {
    const IconComponent = item.icon;
    
    return (
      <tr className={`border-b border-gray-100 hover:bg-gray-50 ${isTotal ? 'bg-gray-50 font-semibold' : ''}`}>
        <td className="py-3 px-4 text-sm">
          <div className="flex items-center gap-2">
            {!isTotal && <IconComponent className="h-4 w-4 text-gray-600" />}
            <span className={`${isTotal ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
              {item.label}
            </span>
          </div>
        </td>
        <td className="py-3 px-4 text-sm text-gray-900 text-center">{item.totalOpened}</td>
        <td className="py-3 px-4 text-sm text-gray-900 text-center">{item.totalResolved}</td>
        <td className="py-3 px-4 text-sm text-center">
          <Badge 
            variant="outline" 
            className={`text-xs ${
              item.percentage >= 80 ? 'text-green-600 bg-green-50 border-green-200' :
              item.percentage >= 60 ? 'text-yellow-600 bg-yellow-50 border-yellow-200' :
              'text-red-600 bg-red-50 border-red-200'
            }`}
          >
            {item.percentage}%
          </Badge>
        </td>
      </tr>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          <Timer className="h-5 w-5 text-blue-600" />
          Ticket Summary by Priority
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Incidents Table */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                Incidents
              </h3>
              <Badge variant="outline" className="text-xs">
                 {incidentSummary.overallPercentage}% Resolved
              </Badge>
            </div>
            
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opened
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resolved
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {incidentBreakdown.map((item, index) => (
                    <TableRow key={index} item={item} />
                  ))}
                  <TableRow 
                    item={{
                      label: "Total",
                      icon: null,
                      totalOpened: incidentSummary.totalOpened,
                      totalResolved: incidentSummary.totalResolved,
                      percentage: incidentSummary.overallPercentage
                    }} 
                    isTotal={true}
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* Catalog Tasks Table */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                Catalog Tasks
              </h3>
              <Badge variant="outline" className="text-xs">
               {taskSummary.overallPercentage}% Resolved
              </Badge>
            </div>
            
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opened
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resolved
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {taskBreakdown.map((item, index) => (
                    <TableRow key={index} item={item} />
                  ))}
                  <TableRow 
                    item={{
                      label: "Total",
                      icon: null,
                      totalOpened: taskSummary.totalOpened,
                      totalResolved: taskSummary.totalResolved,
                      percentage: taskSummary.overallPercentage
                    }} 
                    isTotal={true}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Summary */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-600">
              Performance Summary
            </div>
            <div className="text-gray-500">
              Total resolved: {incidentSummary.totalResolved + taskSummary.totalResolved} | Total opened: {incidentSummary.totalOpened + taskSummary.totalOpened} | Overall rate: {Math.round(((incidentSummary.totalResolved + taskSummary.totalResolved) / (incidentSummary.totalOpened + taskSummary.totalOpened)) * 100)}%
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 